/**
 * DOM id of the JSON `<script>` a host app injects into the shell HTML to hand
 * the browser its per-host runtime config.
 *
 * The shell bundle is built once and served verbatim by every host
 * (enrollment-composer, verify, the microsites, ...), so anything that differs
 * per host — the same-origin API mount, the CDN the module loader pulls from,
 * and the carrier theme — cannot be baked into the bundle. Instead the serving
 * host serializes it into a `<script type="application/json" id="...">` block
 * and the browser entry reads it on boot.
 *
 * Shared here (framework-agnostic core) so the injecting server and the reading
 * client entry can never drift on the id. See `SerializedShellConfig` and
 * `readSerializedShellConfig` in `@bestowinc/tinkertoy-client` (consumer) and
 * `getShellHtml` in enrollment-composer (producer).
 */
export const SHELL_CONFIG_ELEMENT_ID = "tinkertoy-shell-config";

/**
 * GCS object key for the shell HTML template that host apps fetch server-side
 * and inject per-host runtime config into before serving to the browser.
 *
 * Intentionally **not** `index.html` so the object is never the CDN bucket's
 * default web page. Deployed with a non-HTML content type so direct browser
 * requests are unlikely to render a bootable shell (host injection is still
 * required).
 */
export const SHELL_HTML_CDN_OBJECT_KEY = "shell-template.html";

/**
 * Host-augmentable browser telemetry wire shape for the shell config JSON blob.
 *
 * Augment via `declare module "@bestowinc/tinkertoy-core"` in
 * `@bestowinc/tinkertoy-bestow-types`. When no augmentation is loaded the
 * interface is empty and hosts may omit `telemetry` from the injected config.
 *
 * @see {@link BrowserTelemetryConfig} usage in `InjectedShellConfig` /
 * {@link SerializedShellConfig} on the producer and consumer sides.
 */
// eslint-disable-next-line @typescript-eslint/no-empty-interface, @typescript-eslint/no-empty-object-type -- augmentation target; empty until host packages register telemetry vendors
export interface BrowserTelemetryConfig {}
